import { useCallback, useMemo } from 'react';

import {
  use2DWinchMovement,
  useCameraPanTilt,
  useCameraRotationPanLeft,
  useCameraRotationPanRight,
  useCameraRotationStop,
  useCameraRotationTiltDown,
  useCameraRotationTiltUp,
  useCameraZoomIn,
  useCameraZoomOut,
  useMoveWinchDown,
  useMoveWinchIn,
  useMoveWinchOut,
  useMoveWinchStop,
  useMoveWinchUp,
} from '@restDataAccess';
import { CameraInfo } from '@scaleaq/sitecontrol-lib';

import {
  getMovementFeedbackFromPanTilt,
  getMovementFeedbackFromWinch2D,
  movementFeedbackMutateOptions,
} from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraMovementFeedback/cameraMovementFeedback.helpers.ts';
import type { CameraMovementFeedbackKind } from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraMovementFeedback/cameraMovementFeedback.types.ts';
import type { Hotkey } from '@/hooks/shortcutHooks/hotkeys';
import {
  CameraControlHotkeyNames,
  WinchControlHotkeyNames,
} from '@/hooks/shortcutHooks/hotkeys/types.ts';
import type { CameraControlHotKeyDefinitionsResult } from '@/hooks/shortcutHooks/useHotkeyDefinitions/useCameraControlHotKeyDefinitions.tsx';
import type { WinchControlHotkeysDefinitionsResult } from '@/hooks/shortcutHooks/useHotkeyDefinitions/useWinchControlHotkeysDefinitions.tsx';

type CameraAndWinchMovementHotkeys = Pick<
  CameraControlHotKeyDefinitionsResult,
  'cameraControlHoldRepeatHotkeys'
> & {
  winchControlHotkeys: WinchControlHotkeysDefinitionsResult;
};

const winchMovementSpeed = 50;
const cameraRotationSpeed = 50;

type CameraAndWinchMovementOptions = {
  /** Stream overlay callback; omit when movement is not shown on a camera widget (e.g. floating panel). */
  onMovementSuccess?: (kind: CameraMovementFeedbackKind) => void;
};

export const useCameraAndWinchMovement = (
  camera: CameraInfo | undefined,
  { cameraControlHoldRepeatHotkeys, winchControlHotkeys }: CameraAndWinchMovementHotkeys,
  { onMovementSuccess }: CameraAndWinchMovementOptions = {}
) => {
  const cameraId = camera?.id;

  // camera rotation handlers
  const { mutate: tiltUp } = useCameraRotationTiltUp();
  const { mutate: tiltDown } = useCameraRotationTiltDown();
  const { mutate: panLeft } = useCameraRotationPanLeft();
  const { mutate: panRight } = useCameraRotationPanRight();
  const { mutate: stopRotation } = useCameraRotationStop();
  const { mutate: zoomIn } = useCameraZoomIn();
  const { mutate: zoomOut } = useCameraZoomOut();
  const { mutate: panTilt } = useCameraPanTilt();

  const onCameraPanOrTilt = useCallback(
    ({ pan, tilt }: { pan: number; tilt: number }) => {
      if (cameraId == null) return;
      const kind = getMovementFeedbackFromPanTilt(pan, tilt);
      panTilt(
        {
          inventoryId: cameraId,
          pan,
          tilt,
        },
        kind ? movementFeedbackMutateOptions(onMovementSuccess, kind) : undefined
      );
    },
    [cameraId, onMovementSuccess, panTilt]
  );

  const onTiltCameraUp = useCallback(() => {
    if (cameraId == null) return;
    tiltUp(
      {
        inventoryId: cameraId,
        speed: cameraRotationSpeed,
      },
      movementFeedbackMutateOptions(onMovementSuccess, 'tiltUp')
    );
  }, [cameraId, onMovementSuccess, tiltUp]);

  const onTiltCameraDown = useCallback(() => {
    if (cameraId == null) return;
    tiltDown(
      { inventoryId: cameraId, speed: cameraRotationSpeed },
      movementFeedbackMutateOptions(onMovementSuccess, 'tiltDown')
    );
  }, [cameraId, onMovementSuccess, tiltDown]);
  const onPanCameraRight = useCallback(() => {
    if (cameraId == null) return;
    panRight(
      { inventoryId: cameraId, speed: cameraRotationSpeed },
      movementFeedbackMutateOptions(onMovementSuccess, 'panRight')
    );
  }, [cameraId, onMovementSuccess, panRight]);

  const onPanCameraLeft = useCallback(() => {
    if (cameraId == null) return;
    panLeft(
      { inventoryId: cameraId, speed: cameraRotationSpeed },
      movementFeedbackMutateOptions(onMovementSuccess, 'panLeft')
    );
  }, [cameraId, onMovementSuccess, panLeft]);

  const onStopCameraRotation = useCallback(() => {
    if (cameraId == null) return;
    stopRotation({ inventoryId: cameraId });
  }, [cameraId, stopRotation]);

  const onZoomCameraIn = useCallback(() => {
    if (cameraId == null) return;
    zoomIn(
      { inventoryId: cameraId, speed: cameraRotationSpeed },
      movementFeedbackMutateOptions(onMovementSuccess, 'zoomIn')
    );
  }, [cameraId, onMovementSuccess, zoomIn]);

  const onZoomCameraOut = useCallback(() => {
    if (cameraId == null) return;
    zoomOut(
      { inventoryId: cameraId, speed: cameraRotationSpeed },
      movementFeedbackMutateOptions(onMovementSuccess, 'zoomOut')
    );
  }, [cameraId, onMovementSuccess, zoomOut]);
  const startCameraRotation = useMemo(
    () => ({
      ...keyboardKeyHandlersForHotkey(
        cameraControlHoldRepeatHotkeys[CameraControlHotkeyNames.TiltCameraUp],
        () => onTiltCameraUp()
      ),
      ...keyboardKeyHandlersForHotkey(
        cameraControlHoldRepeatHotkeys[CameraControlHotkeyNames.TiltCameraDown],
        () => onTiltCameraDown()
      ),
      ...keyboardKeyHandlersForHotkey(
        cameraControlHoldRepeatHotkeys[CameraControlHotkeyNames.PanCameraLeft],
        () => onPanCameraLeft()
      ),
      ...keyboardKeyHandlersForHotkey(
        cameraControlHoldRepeatHotkeys[CameraControlHotkeyNames.PanCameraRight],
        () => onPanCameraRight()
      ),
    }),
    [
      cameraControlHoldRepeatHotkeys,
      onTiltCameraUp,
      onTiltCameraDown,
      onPanCameraLeft,
      onPanCameraRight,
    ]
  );

  // winch movement handlers
  const { mutate: moveWinchUp } = useMoveWinchUp();
  const { mutate: moveWinchDown } = useMoveWinchDown();
  const { mutate: moveWinchIn } = useMoveWinchIn();
  const { mutate: moveWinchOut } = useMoveWinchOut();
  const { mutate: stopWinchMovement } = useMoveWinchStop();
  const { mutate: moveWinch } = use2DWinchMovement();

  const on2DWinchMove = useCallback(
    (horizontalSpeed: number, verticalSpeed: number) => {
      if (cameraId == null) return;
      const kind = getMovementFeedbackFromWinch2D(horizontalSpeed, verticalSpeed);
      moveWinch(
        {
          cameraId,
          verticalSpeed,
          horizontalSpeed,
        },
        kind ? movementFeedbackMutateOptions(onMovementSuccess, kind) : undefined
      );
    },
    [cameraId, moveWinch, onMovementSuccess]
  );

  const onMoveWinchUp = useCallback(() => {
    if (cameraId == null) return;
    moveWinchUp(
      { cameraId, speed: winchMovementSpeed },
      movementFeedbackMutateOptions(onMovementSuccess, 'winchUp')
    );
  }, [cameraId, moveWinchUp, onMovementSuccess]);

  const onMoveWinchDown = useCallback(() => {
    if (cameraId == null) return;
    moveWinchDown(
      { cameraId, speed: winchMovementSpeed },
      movementFeedbackMutateOptions(onMovementSuccess, 'winchDown')
    );
  }, [cameraId, moveWinchDown, onMovementSuccess]);
  const onMoveWinchIn = useCallback(() => {
    if (cameraId == null) return;
    moveWinchIn(
      { cameraId, speed: winchMovementSpeed },
      movementFeedbackMutateOptions(onMovementSuccess, 'winchIn')
    );
  }, [cameraId, moveWinchIn, onMovementSuccess]);

  const onMoveWinchOut = useCallback(() => {
    if (cameraId == null) return;
    moveWinchOut(
      { cameraId, speed: winchMovementSpeed },
      movementFeedbackMutateOptions(onMovementSuccess, 'winchOut')
    );
  }, [cameraId, moveWinchOut, onMovementSuccess]);
  const onMoveWinchStop = useCallback(() => {
    if (cameraId == null) return;
    stopWinchMovement({ cameraId });
  }, [cameraId, stopWinchMovement]);

  const startWinch = useMemo(
    () => ({
      ...keyboardKeyHandlersForHotkey(
        winchControlHotkeys[WinchControlHotkeyNames.MoveWinchUp],
        () => onMoveWinchUp()
      ),
      ...keyboardKeyHandlersForHotkey(
        winchControlHotkeys[WinchControlHotkeyNames.MoveWinchDown],
        () => onMoveWinchDown()
      ),
      ...keyboardKeyHandlersForHotkey(
        winchControlHotkeys[WinchControlHotkeyNames.MoveWinchIn],
        () => onMoveWinchIn()
      ),
      ...keyboardKeyHandlersForHotkey(
        winchControlHotkeys[WinchControlHotkeyNames.MoveWinchOut],
        () => onMoveWinchOut()
      ),
    }),
    [winchControlHotkeys, onMoveWinchDown, onMoveWinchIn, onMoveWinchOut, onMoveWinchUp]
  );

  const stopAllCameraRotation = useMemo(
    () => ({
      ...keyboardKeyHandlersForHotkey(
        cameraControlHoldRepeatHotkeys[CameraControlHotkeyNames.TiltCameraUp],
        () => onStopCameraRotation()
      ),
      ...keyboardKeyHandlersForHotkey(
        cameraControlHoldRepeatHotkeys[CameraControlHotkeyNames.TiltCameraDown],
        () => onStopCameraRotation()
      ),
      ...keyboardKeyHandlersForHotkey(
        cameraControlHoldRepeatHotkeys[CameraControlHotkeyNames.PanCameraLeft],
        () => onStopCameraRotation()
      ),
      ...keyboardKeyHandlersForHotkey(
        cameraControlHoldRepeatHotkeys[CameraControlHotkeyNames.PanCameraRight],
        () => onStopCameraRotation()
      ),
    }),
    [cameraControlHoldRepeatHotkeys, onStopCameraRotation]
  );

  const stopWinchAll = useMemo(
    () => ({
      ...keyboardKeyHandlersForHotkey(
        winchControlHotkeys[WinchControlHotkeyNames.MoveWinchUp],
        () => onMoveWinchStop()
      ),
      ...keyboardKeyHandlersForHotkey(
        winchControlHotkeys[WinchControlHotkeyNames.MoveWinchDown],
        () => onMoveWinchStop()
      ),
      ...keyboardKeyHandlersForHotkey(
        winchControlHotkeys[WinchControlHotkeyNames.MoveWinchIn],
        () => onMoveWinchStop()
      ),
      ...keyboardKeyHandlersForHotkey(
        winchControlHotkeys[WinchControlHotkeyNames.MoveWinchOut],
        () => onMoveWinchStop()
      ),
    }),
    [winchControlHotkeys, onMoveWinchStop]
  );
  return {
    // camera rotation handlers
    startCameraRotation,
    stopAllCameraRotation,
    onCameraPanOrTilt,
    onPanCameraLeft,
    onPanCameraRight,
    onTiltCameraDown,
    onTiltCameraUp,
    onStopCameraRotation,
    onZoomCameraIn,
    onZoomCameraOut,

    // winch movement handlers
    on2DWinchMove,
    startWinch,
    stopWinchAll,
    onMoveWinchIn,
    onMoveWinchOut,
    onMoveWinchUp,
    onMoveWinchDown,
    onMoveWinchStop,
  };
};

export type CameraAndWinchMovement = ReturnType<typeof useCameraAndWinchMovement>;

/** Gamepad / repeat maps keyed by `keyboardKey`; include optional WASD-style alternates when set. */
const keyboardKeyHandlersForHotkey = (
  hotkey: Hotkey,
  fn: () => void
): Record<string, () => void> =>
  hotkey.optionalPrimary
    ? {
        [hotkey.primary.keyboardKey]: fn,
        [hotkey.optionalPrimary.keyboardKey]: fn,
      }
    : { [hotkey.primary.keyboardKey]: fn };
